import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * ТЗ V5 №15: плашка с пояснением. Открывается наведением, фокусом с
 * клавиатуры и нажатием (на телефоне hover нет); закрывается Escape, кликом
 * снаружи и повторным нажатием. Нажатие не всплывает — карточка проблемы под
 * плашкой не открывается.
 *
 * Плашка выводится в body с position: fixed и прижимается к краям окна по
 * обеим осям; если снизу места нет — открывается над триггером. Так она не
 * увеличивает размер страницы и не даёт горизонтальной прокрутки.
 */
const VIEWPORT_GAP = 8;
const OFFSET = 6;

export const placePopover = (trigger, popover, viewport) => {
  let left = trigger.left;
  if (left + popover.width > viewport.width - VIEWPORT_GAP) {
    left = viewport.width - VIEWPORT_GAP - popover.width;
  }
  left = Math.max(VIEWPORT_GAP, left);

  let top = trigger.bottom + OFFSET;
  const fitsBelow = top + popover.height <= viewport.height - VIEWPORT_GAP;
  const above = trigger.top - OFFSET - popover.height;
  if (!fitsBelow && above >= VIEWPORT_GAP) top = above;
  else if (!fitsBelow) top = Math.max(VIEWPORT_GAP, viewport.height - VIEWPORT_GAP - popover.height);
  return { top, left };
};

const IssueTermHelp = ({ term, className = '', children, testId }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [focused, setFocused] = useState(false);
  const [position, setPosition] = useState(null);
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverId = useId();
  const open = hovered || pinned || focused;

  const updatePosition = useCallback(() => {
    const trigger = rootRef.current?.getBoundingClientRect?.();
    const popover = popoverRef.current?.getBoundingClientRect?.();
    if (!trigger || !popover) return;
    const next = placePopover(trigger, popover, {
      width: document.documentElement.clientWidth || window.innerWidth,
      height: document.documentElement.clientHeight || window.innerHeight,
    });
    setPosition((prev) => (
      prev && prev.top === next.top && prev.left === next.left ? prev : next
    ));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!pinned) return undefined;
    const onDocument = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setPinned(false);
    };
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('touchstart', onDocument);
    return () => {
      document.removeEventListener('mousedown', onDocument);
      document.removeEventListener('touchstart', onDocument);
    };
  }, [pinned]);

  const close = () => {
    setPinned(false);
    setHovered(false);
    setFocused(false);
  };

  return (
    <span
      ref={rootRef}
      className="oi-term"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        className={`oi-term-trigger ${className}`.trim()}
        data-testid={testId}
        aria-expanded={open}
        aria-describedby={open ? popoverId : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={(event) => {
          event.stopPropagation();
          // Повторное нажатие закрывает и то, что открыл фокус.
          if (open && !pinned) close();
          else setPinned((value) => !value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            close();
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.stopPropagation();
          }
        }}
      >
        {children}
      </button>
      {open && createPortal(
        <span
          ref={popoverRef}
          className="oi-term-popover"
          role="tooltip"
          id={popoverId}
          // Портал всплывает по дереву React: клик по плашке не должен
          // открыть карточку проблемы под ней.
          onClick={(event) => event.stopPropagation()}
          // До первого замера плашка невидима: иначе мигнула бы в углу окна.
          style={position
            ? { top: position.top, left: position.left }
            : { top: 0, left: 0, visibility: 'hidden' }}
        >
          {/* Только описание: что это за значок и его значение, видно на триггере. */}
          <span className="oi-term-text">{term.description}</span>
        </span>,
        document.body
      )}
    </span>
  );
};

export default IssueTermHelp;
